"use client";

import { WhatsAppText } from "@commerce/conversations/whatsapp/WhatsAppText";
import { formatMoney } from "@commerce/shopper/types";
import { cn } from "@ui/lib";
import {
	CheckIcon,
	ExternalLinkIcon,
	FileTextIcon,
	ImageIcon,
	ListIcon,
	MapPinIcon,
	PhoneIcon,
	ReplyIcon,
	ShoppingBagIcon,
	UserRoundIcon,
} from "lucide-react";
import { createContext, type ReactNode, use } from "react";
import { withContentKeys } from "./content-keys";

/*
 * A drawing of a WhatsApp message from the exact payload we sent (the Graph body without the
 * recipient, as `ChannelMessage.payload` keeps it) or an `OutboundPlan` part: what the shopper
 * saw, never our own shopper chat. Unknown shapes fall back to "Sent a <kind>".
 */

type Json = Record<string, unknown>;

/** A product a message named, as the shopper saw it (resolved from its retailer id). */
export interface PreviewProduct {
	title: string;
	imageUrl: string | null;
	price: string | null;
	currency: string | null;
}

/**
 * The products the drawn messages name, by `product_retailer_id` (conversations.get's
 * `channelProducts`, preview.render's `products`). Without it, a product shows its id.
 */
export const WhatsAppProducts = createContext<Record<string, PreviewProduct>>(
	{},
);

export const asObject = (value: unknown): Json =>
	value && typeof value === "object" && !Array.isArray(value)
		? (value as Json)
		: {};
export const asString = (value: unknown): string | null =>
	typeof value === "string" && value.length > 0 ? value : null;
export const asArray = (value: unknown): unknown[] =>
	Array.isArray(value) ? value : [];

/** A reply button's own content: Meta requires its id; the title stands in without one. */
function replyContent(reply: Json): string {
	return asString(reply.id) ?? asString(reply.title) ?? "button";
}

/** A carousel card's own content: the product it shows, or its body. */
function cardContent(card: Json): string {
	return (
		asString(asObject(card.action).product_retailer_id) ??
		asString(asObject(card.body).text) ??
		"card"
	);
}

/** A list section's own content: its title (required with more than one), or its first row. */
function sectionContent(section: {
	title: string | null;
	items: { id: string }[];
}): string {
	return section.title ?? section.items[0]?.id ?? "section";
}

/** The `interactive` object of a payload, whether wrapped (`{ type: "interactive" }`) or bare. */
export function interactiveOf(payload: unknown): Json | null {
	const value = asObject(payload);
	if (value.type === "interactive" && value.interactive) {
		return asObject(value.interactive);
	}
	return asString(value.type) && INTERACTIVE_TYPES.has(String(value.type))
		? value
		: null;
}

const INTERACTIVE_TYPES = new Set([
	"button",
	"list",
	"cta_url",
	"location_request_message",
	"request_contact_info",
	"carousel",
	"product_list",
	"product",
	"voice_call",
	"flow",
]);

/** One line on what a message is: "Product list · 2 sections, 6 items". */
export function describePayload(kind: string, payload: unknown): string {
	const interactive = interactiveOf(payload);
	if (!interactive) {
		return KIND_LABEL[kind] ?? `A ${kind.replaceAll("_", " ")}`;
	}
	const action = asObject(interactive.action);
	switch (interactive.type) {
		case "product_list": {
			const sections = asArray(action.sections);
			const items = sections.reduce<number>(
				(sum, section) =>
					sum + asArray(asObject(section).product_items).length,
				0,
			);
			return `Product list · ${plural(sections.length, "section")}, ${plural(items, "item")}`;
		}
		case "list": {
			const rows = asArray(action.sections).reduce<number>(
				(sum, section) => sum + asArray(asObject(section).rows).length,
				0,
			);
			return `List · ${plural(rows, "choice")}`;
		}
		case "carousel": {
			const cards = asArray(action.cards);
			const products = cards.some(
				(card) => asObject(card).type === "product",
			);
			return `${products ? "Product carousel" : "Image carousel"} · ${plural(cards.length, "card")}`;
		}
		case "button":
			return `Buttons · ${plural(asArray(action.buttons).length, "button")}`;
		default:
			return (
				KIND_LABEL[`interactive.${String(interactive.type)}`] ??
				"Interactive message"
			);
	}
}

const KIND_LABEL: Record<string, string> = {
	text: "Text",
	image: "Photo",
	audio: "Audio",
	voice: "Voice note",
	location: "Location",
	reaction: "Reaction",
	contacts: "Contact",
	order: "Cart",
	template: "Template",
	flow_reply: "Form answers",
	"interactive.cta_url": "Link button",
	"interactive.location_request_message": "Location request",
	"interactive.location_request": "Location request",
	"interactive.request_contact_info": "Phone number request",
	"interactive.contact_request": "Phone number request",
	"interactive.product": "Single product",
	"interactive.single_product": "Single product",
	"interactive.voice_call": "Call button",
	"interactive.flow": "Checkout form",
};

const plural = (n: number, word: string) =>
	`${n.toLocaleString("en-US")} ${word}${n === 1 ? "" : "s"}`;

/** WhatsApp's bubble: white card, small radius, header, body, footer, then the buttons. */
export function WhatsAppBubble({
	children,
	buttons,
	align = "start",
	className,
}: {
	children: ReactNode;
	buttons?: ReactNode;
	align?: "start" | "end";
	className?: string;
}) {
	return (
		<div
			className={cn(
				"flex w-full max-w-sm flex-col overflow-hidden rounded-2xl text-sm ring-1 ring-foreground/8",
				align === "end" ? "self-end bg-muted" : "bg-card",
				className,
			)}
		>
			<div className="flex flex-col gap-1.5 px-3 py-2">{children}</div>
			{buttons ? (
				<div className="flex flex-col divide-y divide-border/70 border-border/70 border-t">
					{buttons}
				</div>
			) : null}
		</div>
	);
}

/** A button under a bubble, as WhatsApp draws it; the one the shopper tapped is marked. */
export function BubbleButton({
	icon: Icon = ReplyIcon,
	label,
	tapped = false,
}: {
	icon?: typeof ReplyIcon;
	label: string;
	tapped?: boolean;
}) {
	return (
		<span
			className={cn(
				"flex min-h-9 items-center justify-center gap-1.5 px-3 py-1.5 text-center font-medium",
				tapped ? "text-brand" : "text-foreground/80",
			)}
		>
			{tapped ? (
				<CheckIcon className="size-3.5 shrink-0" aria-hidden />
			) : (
				<Icon className="size-3.5 shrink-0" aria-hidden />
			)}
			<span dir="auto" className="min-w-0 truncate">
				{label}
			</span>
			{tapped ? <span className="sr-only">(tapped)</span> : null}
		</span>
	);
}

function Header({ header }: { header: unknown }) {
	const value = asObject(header);
	if (value.type === "text" && asString(value.text)) {
		return (
			<p dir="auto" className="font-semibold">
				{String(value.text)}
			</p>
		);
	}
	if (value.type === "image") {
		const link = mediaLink(asObject(value.image).link);
		return link ? (
			<img
				src={link}
				alt=""
				loading="lazy"
				className="-mx-2 -mt-1 aspect-[1.91/1] w-[calc(100%+1rem)] max-w-none rounded-xl bg-muted object-cover"
			/>
		) : (
			<MediaPlaceholder />
		);
	}
	return null;
}

/** Only links we can show in the admin: our own routes or https. R2 keys are not links. */
function mediaLink(value: unknown): string | null {
	const link = asString(value);
	return link && (link.startsWith("/") || link.startsWith("https://"))
		? link
		: null;
}

function MediaPlaceholder() {
	return (
		<span className="grid aspect-[1.91/1] w-full place-items-center rounded-xl bg-muted text-muted-foreground">
			<ImageIcon className="size-5" aria-hidden />
		</span>
	);
}

/** An interactive body (`{ text }`), a text message (`{ body }`) or a plain string. */
function Body({ text }: { text: unknown }) {
	const value =
		asString(asObject(text).text) ??
		asString(asObject(text).body) ??
		asString(text);
	return value ? <WhatsAppText text={value} /> : null;
}

function Footer({ footer }: { footer: unknown }) {
	const value = asString(asObject(footer).text);
	return value ? (
		<p dir="auto" className="text-muted-foreground text-xs">
			{value}
		</p>
	) : null;
}

/** An interactive message (`interactive` of a send), with the tapped reply id marked. */
export function InteractivePreview({
	interactive,
	tappedId,
	align,
}: {
	interactive: Json;
	tappedId?: string | null;
	align?: "start" | "end";
}) {
	const action = asObject(interactive.action);
	const common = (
		<>
			<Header header={interactive.header} />
			<Body text={interactive.body} />
			<Footer footer={interactive.footer} />
		</>
	);

	switch (interactive.type) {
		case "button":
			return (
				<WhatsAppBubble
					align={align}
					buttons={withContentKeys(
						asArray(action.buttons).map((entry) =>
							asObject(asObject(entry).reply),
						),
						replyContent,
					).map(({ key, item: reply }) => {
						return (
							<BubbleButton
								key={key}
								label={asString(reply.title) ?? "Button"}
								tapped={
									Boolean(tappedId) && reply.id === tappedId
								}
							/>
						);
					})}
				>
					{common}
				</WhatsAppBubble>
			);
		case "list": {
			const sections = asArray(action.sections).map(asObject);
			return (
				<div className="flex w-full max-w-sm flex-col gap-1.5">
					<WhatsAppBubble
						align={align}
						buttons={
							<BubbleButton
								icon={ListIcon}
								label={asString(action.button) ?? "Choose"}
							/>
						}
					>
						{common}
					</WhatsAppBubble>
					<OptionList
						sections={sections.map((section) => ({
							title: asString(section.title),
							items: asArray(section.rows).map((row) => {
								const value = asObject(row);
								return {
									id: asString(value.id) ?? "",
									title: asString(value.title) ?? "",
									description: asString(value.description),
								};
							}),
						}))}
						tappedId={tappedId}
					/>
				</div>
			);
		}
		case "cta_url": {
			const parameters = asObject(action.parameters);
			return (
				<WhatsAppBubble
					align={align}
					buttons={
						<BubbleButton
							icon={ExternalLinkIcon}
							label={asString(parameters.display_text) ?? "Open"}
						/>
					}
				>
					{common}
				</WhatsAppBubble>
			);
		}
		case "location_request_message":
			return (
				<WhatsAppBubble
					align={align}
					buttons={
						<BubbleButton icon={MapPinIcon} label="Send location" />
					}
				>
					{common}
				</WhatsAppBubble>
			);
		case "request_contact_info":
			return (
				<WhatsAppBubble
					align={align}
					buttons={
						<BubbleButton
							icon={UserRoundIcon}
							label="Share phone number"
						/>
					}
				>
					{common}
				</WhatsAppBubble>
			);
		case "voice_call": {
			const parameters = asObject(action.parameters);
			return (
				<WhatsAppBubble
					align={align}
					buttons={
						<BubbleButton
							icon={PhoneIcon}
							label={
								asString(parameters.display_text) ?? "Call now"
							}
						/>
					}
				>
					{common}
				</WhatsAppBubble>
			);
		}
		case "flow": {
			const parameters = asObject(action.parameters);
			return (
				<WhatsAppBubble
					align={align}
					buttons={
						<BubbleButton
							icon={FileTextIcon}
							label={asString(parameters.flow_cta) ?? "Open form"}
						/>
					}
				>
					{common}
				</WhatsAppBubble>
			);
		}
		case "product":
			return (
				<WhatsAppBubble
					align={align}
					buttons={
						<BubbleButton icon={ShoppingBagIcon} label="View" />
					}
				>
					<ProductTile id={asString(action.product_retailer_id)} />
					{common}
				</WhatsAppBubble>
			);
		case "product_list": {
			const sections = asArray(action.sections).map(asObject);
			const items = sections.reduce(
				(sum, section) => sum + asArray(section.product_items).length,
				0,
			);
			return (
				<div className="flex w-full max-w-sm flex-col gap-1.5">
					<WhatsAppBubble
						align={align}
						buttons={
							<BubbleButton
								icon={ShoppingBagIcon}
								label={`View items (${items})`}
							/>
						}
					>
						{common}
					</WhatsAppBubble>
					<OptionList
						sections={sections.map((section) => ({
							title: asString(section.title),
							items: asArray(section.product_items).map(
								(item) => {
									const id =
										asString(
											asObject(item).product_retailer_id,
										) ?? "";
									return { id, title: id, description: null };
								},
							),
						}))}
						products
					/>
				</div>
			);
		}
		case "carousel":
			return (
				<div className="flex w-full min-w-0 max-w-sm flex-col gap-1.5">
					<WhatsAppBubble align={align}>{common}</WhatsAppBubble>
					<ul className="flex snap-x gap-2 overflow-x-auto pb-1">
						{withContentKeys(
							asArray(action.cards).map(asObject),
							cardContent,
						).map(({ key, item: card }) => {
							const productId = asString(
								asObject(card.action).product_retailer_id,
							);
							return (
								<li
									key={key}
									className="w-44 shrink-0 snap-start"
								>
									<WhatsAppBubble
										buttons={
											card.type === "product" ? (
												<BubbleButton
													icon={ShoppingBagIcon}
													label="View"
												/>
											) : (
												withContentKeys(
													asArray(
														asObject(card.action)
															.buttons,
													).map((button) =>
														asObject(
															asObject(button)
																.quick_reply,
														),
													),
													replyContent,
												).map(
													({ key, item: reply }) => {
														return (
															<BubbleButton
																key={key}
																label={
																	asString(
																		reply.title,
																	) ??
																	"Button"
																}
																tapped={
																	Boolean(
																		tappedId,
																	) &&
																	reply.id ===
																		tappedId
																}
															/>
														);
													},
												)
											)
										}
									>
										{card.type === "product" ? (
											<ProductTile id={productId} />
										) : (
											<>
												<Header header={card.header} />
												<Body text={card.body} />
											</>
										)}
									</WhatsAppBubble>
								</li>
							);
						})}
					</ul>
				</div>
			);
		default:
			return (
				<WhatsAppBubble align={align}>
					{common}
					<p className="text-muted-foreground text-xs">
						{describePayload(
							`interactive.${String(interactive.type)}`,
							{
								type: "interactive",
								interactive,
							},
						)}
					</p>
				</WhatsAppBubble>
			);
	}
}

/**
 * A catalog product as WhatsApp draws it from the Meta catalog: name, picture and price. Only
 * a product since removed falls back to its retailer id.
 */
function ProductTile({ id }: { id: string | null }) {
	const product = use(WhatsAppProducts)[id ?? ""];
	if (!product) {
		return (
			<span className="flex items-center gap-2 rounded-xl bg-muted px-2 py-2 text-muted-foreground text-xs">
				<ShoppingBagIcon className="size-4 shrink-0" aria-hidden />
				<span className="min-w-0 truncate font-mono">
					{id ?? "Catalog product"}
				</span>
			</span>
		);
	}
	return (
		<span className="flex items-center gap-2 rounded-xl bg-muted px-2 py-2 text-xs">
			<span className="grid size-9 shrink-0 place-items-center overflow-hidden rounded-lg bg-card ring-1 ring-foreground/6">
				{product.imageUrl ? (
					<img
						src={product.imageUrl}
						alt=""
						loading="lazy"
						className="size-full object-contain p-0.5"
					/>
				) : (
					<ShoppingBagIcon
						className="size-4 text-muted-foreground"
						aria-hidden
					/>
				)}
			</span>
			<span className="flex min-w-0 flex-col">
				<span dir="auto" className="truncate font-medium">
					{product.title}
				</span>
				{product.price && product.currency ? (
					<span className="text-muted-foreground tabular-nums">
						{formatMoney(product.price, product.currency)}
					</span>
				) : null}
			</span>
		</span>
	);
}

/** The rows a list or product list opens, grouped by section; the tapped row is marked. */
function OptionList({
	sections,
	tappedId,
	products = false,
}: {
	sections: {
		title: string | null;
		items: { id: string; title: string; description: string | null }[];
	}[];
	tappedId?: string | null;
	/**
	 * Product lists: each row is a product, drawn by name and price; when none of them can be
	 * named any more, a section shows its title and how many products.
	 */
	products?: boolean;
}) {
	const known = use(WhatsAppProducts);
	const collapsedSummary =
		products &&
		!sections.some((section) =>
			section.items.some((item) => known[item.id]),
		);
	return (
		<div className="flex w-full max-w-sm flex-col divide-y divide-border/70 rounded-2xl bg-card px-3 text-sm ring-1 ring-foreground/6">
			{withContentKeys(sections, sectionContent).map(
				({ key, item: section }) => (
					<div key={key} className="flex flex-col gap-1 py-2">
						{section.title ? (
							<p
								dir="auto"
								className="font-medium text-muted-foreground text-xs"
							>
								{section.title}
								{collapsedSummary
									? ` · ${plural(section.items.length, "product")}`
									: null}
							</p>
						) : null}
						{collapsedSummary ? null : (
							<ul className="flex flex-col gap-1">
								{section.items.map((item) => {
									const tapped =
										Boolean(tappedId) &&
										item.id === tappedId;
									if (products) {
										return (
											<li key={item.id}>
												<ProductTile id={item.id} />
											</li>
										);
									}
									return (
										<li
											key={item.id || item.title}
											className={cn(
												"flex items-start gap-2",
												tapped && "text-brand",
											)}
										>
											<span className="min-w-0 flex-1">
												<span
													dir="auto"
													className="block truncate"
												>
													{item.title}
												</span>
												{item.description ? (
													<span
														dir="auto"
														className="block truncate text-muted-foreground text-xs"
													>
														{item.description}
													</span>
												) : null}
											</span>
											{tapped ? (
												<CheckIcon
													className="mt-0.5 size-3.5 shrink-0"
													aria-label="Tapped"
												/>
											) : null}
										</li>
									);
								})}
							</ul>
						)}
					</div>
				),
			)}
		</div>
	);
}

/** A template send: its name and language, then its body parameters in order. */
export function TemplatePreview({
	payload,
	align,
}: {
	payload: unknown;
	align?: "start" | "end";
}) {
	const template = asObject(asObject(payload).template);
	const name = asString(template.name) ?? "Template";
	const language = asString(asObject(template.language).code);
	const values = asArray(template.components).flatMap((component) =>
		asArray(asObject(component).parameters).flatMap(
			(parameter) => asString(asObject(parameter).text) ?? [],
		),
	);
	return (
		<WhatsAppBubble align={align}>
			<p className="flex items-center gap-1.5 font-medium text-xs">
				<FileTextIcon className="size-3.5" aria-hidden />
				<span className="font-mono">{name}</span>
				{language ? (
					<span className="text-muted-foreground">{language}</span>
				) : null}
			</p>
			{values.length ? (
				<ul className="flex flex-col gap-0.5 text-muted-foreground text-xs">
					{values.map((value, index) => (
						<li key={`${index}-${value}`} dir="auto">
							{`{{${index + 1}}}`} {value}
						</li>
					))}
				</ul>
			) : null}
		</WhatsAppBubble>
	);
}

/**
 * One outbound message of any kind, from its exact payload. `mediaUrl` is the org-member route
 * for a stored photo or voice note; `tappedId` marks the shopper's answer to buttons or a list.
 */
export function WhatsAppPreview({
	kind,
	payload,
	mediaUrl,
	tappedId,
	align = "start",
}: {
	kind: string;
	payload: unknown;
	mediaUrl?: string | null;
	tappedId?: string | null;
	align?: "start" | "end";
}) {
	const interactive = interactiveOf(payload);
	if (interactive) {
		return (
			<InteractivePreview
				interactive={interactive}
				tappedId={tappedId}
				align={align}
			/>
		);
	}
	const value = asObject(payload);
	const type = asString(value.type) ?? kind;
	if (type === "template") {
		return <TemplatePreview payload={payload} align={align} />;
	}
	if (type === "text") {
		return (
			<WhatsAppBubble align={align}>
				<Body text={value.text} />
			</WhatsAppBubble>
		);
	}
	if (type === "image") {
		const image = asObject(value.image);
		const src = mediaUrl ?? mediaLink(image.link);
		return (
			<WhatsAppBubble align={align}>
				{src ? (
					<img
						src={src}
						alt=""
						loading="lazy"
						className="-mx-2 -mt-1 max-h-72 w-[calc(100%+1rem)] max-w-none rounded-xl bg-muted object-cover"
					/>
				) : (
					<MediaPlaceholder />
				)}
				<Body text={image.caption} />
			</WhatsAppBubble>
		);
	}
	if (type === "location") {
		const location = asObject(value.location);
		return (
			<WhatsAppBubble align={align}>
				<LocationLines
					name={asString(location.name)}
					address={asString(location.address)}
					latitude={location.latitude}
					longitude={location.longitude}
				/>
			</WhatsAppBubble>
		);
	}
	if (type === "reaction") {
		const emoji = asString(asObject(value.reaction).emoji);
		return (
			<span className="text-muted-foreground text-xs">
				{emoji ? `Reacted ${emoji}` : "Removed a reaction"}
			</span>
		);
	}
	return (
		<WhatsAppBubble align={align}>
			<p className="text-muted-foreground text-xs">
				Sent {describePayload(kind, payload).toLowerCase()}
			</p>
		</WhatsAppBubble>
	);
}

/** A pin: its name and address when shared, the coordinates, and a link to open it. */
export function LocationLines({
	name,
	address,
	latitude,
	longitude,
}: {
	name: string | null;
	address: string | null;
	latitude: unknown;
	longitude: unknown;
}) {
	const lat = Number(latitude);
	const lng = Number(longitude);
	const valid = Number.isFinite(lat) && Number.isFinite(lng);
	return (
		<span className="flex items-start gap-2">
			<MapPinIcon className="mt-0.5 size-4 shrink-0" aria-hidden />
			<span className="flex min-w-0 flex-col">
				<span dir="auto" className="font-medium">
					{name ?? "Shared location"}
				</span>
				{address ? (
					<span dir="auto" className="text-muted-foreground text-xs">
						{address}
					</span>
				) : null}
				{valid ? (
					<a
						href={`https://www.openstreetmap.org/?mlat=${lat}&mlon=${lng}#map=17/${lat}/${lng}`}
						target="_blank"
						rel="noreferrer"
						className="text-xs tabular underline-offset-2 hover:underline"
					>
						{lat.toFixed(5)}, {lng.toFixed(5)}
					</a>
				) : null}
			</span>
		</span>
	);
}

/**
 * A whole `OutboundPlan` (spec 3.10), as the test sheet and the playground draw it: the
 * reaction, the lead message, then the interactive one.
 */
export function OutboundPlanPreview({ plan }: { plan: unknown }) {
	const value = asObject(plan);
	const react = asObject(value.react);
	const lead = asObject(value.lead);
	const interactive = asObject(value.interactive);
	return (
		<div className="flex flex-col gap-2">
			{asString(react.emoji) ? (
				<span className="text-muted-foreground text-xs">
					Reacts {String(react.emoji)}
				</span>
			) : null}
			{lead.type === "text" ? (
				<WhatsAppBubble>
					<Body text={lead.text} />
				</WhatsAppBubble>
			) : lead.type === "voice" ? (
				<WhatsAppBubble>
					<p className="text-muted-foreground text-xs">Voice note</p>
					<Body text={lead.text} />
				</WhatsAppBubble>
			) : lead.type === "location" ? (
				<WhatsAppBubble>
					<LocationLines
						name={asString(asObject(lead.place).name)}
						address={asString(asObject(lead.place).address)}
						latitude={asObject(lead.place).latitude}
						longitude={asObject(lead.place).longitude}
					/>
				</WhatsAppBubble>
			) : null}
			{interactive.message ? (
				<InteractivePreview
					interactive={asObject(interactive.message)}
				/>
			) : null}
		</div>
	);
}
